import type { TenantBrandingEntry } from "./api/client";
import { BRAND_APP_DEFAULT_NAME } from "./config/brand";

export const TENANT_BRANDING_CHANGED_EVENT = "aioscp:tenant-branding-changed";

const DEFAULT_PRIMARY_COLOR = "#6366f1";
const DEFAULT_DOCUMENT_TITLE = `${BRAND_APP_DEFAULT_NAME} - Control plane`;
const defaultIconHrefs = new WeakMap<HTMLLinkElement, string>();

type Rgb = readonly [number, number, number];

function normalizeHexColor(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const shortMatch = /^#?([0-9a-fA-F]{3})$/.exec(trimmed);
  if (shortMatch) {
    const [red, green, blue] = shortMatch[1].split("");
    return `#${red}${red}${green}${green}${blue}${blue}`.toLowerCase();
  }
  const longMatch = /^#?([0-9a-fA-F]{6})$/.exec(trimmed);
  return longMatch ? `#${longMatch[1].toLowerCase()}` : null;
}

function hexToRgb(hex: string): Rgb {
  const normalized = normalizeHexColor(hex) ?? DEFAULT_PRIMARY_COLOR;
  return [
    Number.parseInt(normalized.slice(1, 3), 16),
    Number.parseInt(normalized.slice(3, 5), 16),
    Number.parseInt(normalized.slice(5, 7), 16),
  ];
}

function mix(left: Rgb, right: Rgb, rightWeight: number): Rgb {
  const leftWeight = 1 - rightWeight;
  return [
    Math.round(left[0] * leftWeight + right[0] * rightWeight),
    Math.round(left[1] * leftWeight + right[1] * rightWeight),
    Math.round(left[2] * leftWeight + right[2] * rightWeight),
  ];
}

function rgbToken(rgb: Rgb): string {
  return rgb.join(" ");
}

function buildBrandPalette(primaryColor: string): Record<string, string> {
  const primary = hexToRgb(primaryColor);
  const white: Rgb = [255, 255, 255];
  const black: Rgb = [0, 0, 0];

  return {
    "50": rgbToken(mix(primary, white, 0.92)),
    "100": rgbToken(mix(primary, white, 0.84)),
    "200": rgbToken(mix(primary, white, 0.7)),
    "300": rgbToken(mix(primary, white, 0.52)),
    "400": rgbToken(mix(primary, white, 0.28)),
    "500": rgbToken(mix(primary, white, 0.08)),
    "600": rgbToken(primary),
    "700": rgbToken(mix(primary, black, 0.18)),
    "800": rgbToken(mix(primary, black, 0.32)),
    "900": rgbToken(mix(primary, black, 0.48)),
  };
}

function applyFavicon(faviconUrl?: string | null): void {
  const href = faviconUrl?.trim();

  document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="shortcut icon"]').forEach((link) => {
    if (!defaultIconHrefs.has(link)) {
      defaultIconHrefs.set(link, link.getAttribute("href") ?? link.href);
    }
    link.setAttribute("href", href || defaultIconHrefs.get(link) || "");
  });
}

function applyThemeColor(primaryColor: string): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = primaryColor;
  }
}

export function applyTenantBranding(branding?: Partial<TenantBrandingEntry> | null): void {
  const primaryColor = normalizeHexColor(branding?.primaryColor) ?? DEFAULT_PRIMARY_COLOR;
  const palette = buildBrandPalette(primaryColor);

  for (const [shade, value] of Object.entries(palette)) {
    document.documentElement.style.setProperty(`--color-brand-${shade}`, value);
  }

  const appName = branding?.appName?.trim() || BRAND_APP_DEFAULT_NAME;
  document.title = appName === BRAND_APP_DEFAULT_NAME ? DEFAULT_DOCUMENT_TITLE : `${appName} - Control plane`;
  applyThemeColor(primaryColor);
  applyFavicon(branding?.faviconUrl);
}

export function dispatchTenantBrandingChanged(branding: TenantBrandingEntry): void {
  window.dispatchEvent(new CustomEvent<TenantBrandingEntry>(TENANT_BRANDING_CHANGED_EVENT, { detail: branding }));
}